<script>
import mixinComponent from '../../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../../Mixins/global'

export default {
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    id: { type: [Number, String], default: null },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    dataTags: { type: Boolean, default: false },
    placeholder: { type: String, default: 'button.select' },
    reference: { type: String, default: '' },
    configOptions: { type: Array, default: null },
    addLabel: { type: Boolean, default: false },
    labelText: { type: String, default: 'Unimaginative label' },
    modelValue: { type: [String, Number] },
    value: { type: [String, Number] },
    preSelectValue: { type: Boolean, default: true },
    domain: { type: Boolean, default: true },
    clearButton: { type: Boolean, default: true },
    searchInput: { type: Boolean, default: true },
    modelModifiers: { type: Object, default: () => ({}) },
  },

  emits: ['update:modelValue', 'update:value', 'transmitted'],

  data: (vm) => ({
    select: null,
    options: {},
    selected: vm.modelValue ?? vm.value ?? null,
    visible: true,
  }),

  computed: {
    labelClasses() {
      return {
        'fs-16': true,
        'form-label-font': true,
        required: true === this.required,
      }
    },

    select2Options: function () {
      let optionComplement = {
        allowClear: this.clearButton,
        minimumResultsForSearch: this.searchInput ? 0 : -1,
        templateSelection: this.formatSelect2Option,
        templateResult: this.formatSelect2Option,
        allowHtml: true,
        language: 'fr',
        width: '100%',
      }

      if (this.domain) {
        optionComplement['placeholder'] = this.$t(this.placeholder)
      }

      if (this.dataTags) {
        optionComplement['tags'] = true
      }

      optionComplement['data'] = this.buildSelect2Data()
      if (this.configOptions !== null) {
        optionComplement['choices'] = this.configOptions
      }

      const parentModal = this.getParentModalElement()
      if (parentModal) {
        optionComplement['dropdownParent'] = $(parentModal)
      }

      if (this.multiple) {
        optionComplement['theme'] = 'classic'
      }

      return { ...this.options, ...optionComplement }
    },
  },

  methods: {
    applyModifiers(value) {
      if (this.modelModifiers.number) {
        return Number(value)
      }
      return value
    },

    formatSelect2Option(data) {
      const originalOption = data.element
      const escapedText = this.escapeHtmlText(data.text)
      const icon = this.getDataAttribute(originalOption, 'icon')
      const color = this.getDataAttribute(originalOption, 'color')

      const span = document.createElement('span')
      span.innerHTML = `${escapedText} <i class="${icon}" style="color:${color}"></i>`
      return span
    },

    escapeHtmlText(text) {
      const tempDiv = document.createElement('div')
      tempDiv.textContent = text
      return tempDiv.innerHTML
    },

    getDataAttribute(element, attribute) {
      return element?.dataset?.[attribute] ?? ''
    },

    buildSelect2Data() {
      if (this.configOptions === null) {
        return [{ id: '' }]
      }

      return this.configOptions.map((opt) => ({
        id: opt.id,
        text: opt.name,
        visible: opt.visible ?? true,
      }))
    },

    getParentModalElement() {
      const selectElement = this.$refs[this.reference]
      return selectElement?.closest('.modal')
    },

    getSelectedValue() {
      const selectedOption = this.$el.querySelector('select :checked')
      return selectedOption?.value
    },

    initializeSelect2() {
      const selectElement = this.$refs[this.reference]
      if (!selectElement) {
        console.warn('SimpleGroupSelect: select element not found', this.reference)
        return
      }

      this.destroyExistingSelect2()
      this.createSelect2Instance(selectElement)
      this.setInitialValue()
      this.emitPreselectedValue()
      this.attachChangeListener()
    },

    destroyExistingSelect2() {
      if (this.select) {
        try {
          this.select.off().select2('destroy')
        } catch (e) {
          console.warn('SimpleGroupSelect: Error destroying existing Select2:', e)
        }
      }
    },

    createSelect2Instance(selectElement) {
      this.select = $(selectElement).select2(this.select2Options)
    },

    setInitialValue() {
      const value = this.modelValue ?? this.value
      if (value || !isNaN(parseInt(value))) {
        this.select.val(value).trigger('change')
      }
    },

    emitPreselectedValue() {
      const selectedValue = this.getSelectedValue()
      if (this.preSelectValue && !!selectedValue) {
        const modifiedValue = this.applyModifiers(selectedValue)
        this.$emit('update:modelValue', modifiedValue)
        this.$emit('update:value', modifiedValue)
        this.$emit('transmitted', modifiedValue)
      }
    },

    attachChangeListener() {
      this.select.on('change', (event) => {
        const selectedValue = event.target.value

        this.selected = selectedValue
        const modifiedValue = this.applyModifiers(selectedValue)
        this.$emit('update:modelValue', modifiedValue)
        this.$emit('update:value', modifiedValue)
        this.$emit('transmitted', modifiedValue)
      })
    },

    setupModalEventListeners() {
      const modalElement = this.$el.closest('.modal')
      if (!modalElement) {
        return
      }

      $(modalElement).on('shown.bs.modal.simpleGroupSelect', () => {
        this.$nextTick(() => {
          setTimeout(() => {
            this.initializeSelect2()
          }, 50)
        })
      })
    },

    removeModalEventListeners() {
      const modalElement = this.$el.closest('.modal')
      if (modalElement) {
        $(modalElement).off('shown.bs.modal.simpleGroupSelect')
      }
    },

    reinitializeSelect2WithOptions() {
      this.select.select2(this.select2Options)
    },

    updateSelect2ConfigOptions() {
      if (!this.value) {
        this.select.empty()
      }
      this.select.select2(this.select2Options)
    },

    initializeComponent() {
      this.$nextTick(() => {
        this.initializeSelect2()
        this.setupModalEventListeners()
      })
    },
  },

  watch: {
    modelValue: {
      handler(val) {
        if (this.select) {
          this.select.val(val).trigger('change')
        }
      },
    },

    value: {
      handler(val) {
        if (this.select && !this.modelValue) {
          this.select.val(val).trigger('change')
        }
      },
    },

    options: {
      handler() {
        this.reinitializeSelect2WithOptions()
      },
      deep: true,
    },

    configOptions: {
      handler() {
        this.updateSelect2ConfigOptions()
      },
      deep: true,
    },
  },

  mounted() {
    this.initializeComponent()
  },

  beforeUnmount() {
    this.removeModalEventListeners()
  },
}
</script>
<template>
  <div class="form-group my-2">
    <label v-if="addLabel" :class="labelClasses" :for="id" style="font-weight: 500">{{ labelText }}</label>
    <select
      :ref="reference"
      class="form-control"
      v-model="selected"
      :id="id"
      :required="required"
      :disabled="disabled"
      :e2e-select="id"
    >
      <option v-if="visible"></option>
      <slot></slot>
    </select>
  </div>
</template>
